import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Star } from 'lucide-react'
import { Card } from '@/components/ui/card'
import { Avatar } from '@/components/ui/avatar'
import { Skeleton } from '@/components/ui/skeleton'
import { ListPagination } from '@/components/shared/ListPagination'
import { useTechniciansList } from '@/hooks/useTechnicians'
import { toISODate } from '@/components/jobs/agenda/grid-helpers'
import {
  getTechnicianSecondaryStatusDetail,
  isTechnicianCurrentlyOnLeave,
  technicianWorksWeekday,
} from '@/lib/technicians'
import { cn } from '@/lib/utils'
import type { Technician, TechnicianListQuery } from '@/types'

const DAY_LABEL_KEYS = [
  'mon',
  'tue',
  'wed',
  'thu',
  'fri',
  'sat',
  'sun',
] as const

const AVATAR_GRADIENTS = [
  'linear-gradient(149.66deg, #ebf1fe 0%, #2e5ce6 100%)',
  'linear-gradient(149.66deg, #e2f5f3 0%, #0e8a7a 100%)',
  'linear-gradient(149.66deg, #f2ebfd 0%, #7b3bea 100%)',
  'linear-gradient(149.66deg, #fcf2dc 0%, #c87b08 100%)',
  'linear-gradient(149.66deg, #fdeaea 0%, #d02f2f 100%)',
] as const

type ViewMode = 'day' | 'week'
type DayStatusTone =
  | 'available'
  | 'duringIntervention'
  | 'unavailable'
  | 'inactive'
  | 'nonWorking'
type WeekChipTone = 'available' | 'inactive' | 'nonWorking'

const thClass =
  'h-10 border-b border-[#e7e9ef] bg-[#fbfbfc] px-4 py-2 text-left text-[10px] font-medium uppercase leading-[14px] tracking-normal text-[#7b8190]'

const tdClass = 'h-16 border-b border-[#e7e9ef] px-4 py-4 align-middle text-sm'
const technicianNameTextClass =
  'truncate text-[14px] font-medium leading-[1.4] text-[#171a20]'

const statusBadgeClass =
  'inline-flex h-6 w-fit items-center rounded-lg px-2 text-xs font-medium'
const topBadgeClass =
  'inline-flex h-5 items-center gap-1 rounded-md bg-[#fbebbe] px-1.5 text-[11px] font-semibold leading-none text-[#946000]'

const dayStatusToneClass: Record<DayStatusTone, string> = {
  available: 'bg-[#dcfce7] text-[#065f46]',
  duringIntervention: 'bg-[#dbeafe] text-[#356ae6]',
  unavailable: 'bg-[#fef3c7] text-[#b7791f]',
  inactive: 'bg-[#f4f5f8] text-[#3d434e]',
  nonWorking: 'bg-[#f4f5f8] text-[#7b8190]',
}

const weekChipToneClass: Record<WeekChipTone, string> = {
  available: 'bg-[#dcfce7] text-[#065f46]',
  inactive: 'bg-[#fef3c7] text-[#b7791f]',
  nonWorking: 'bg-[#f4f5f8] text-[#7b8190]',
}

function getAvailabilityRowClass(index: number): string {
  if (index === 0) return 'bg-[#f4f5f8]'
  return index % 2 === 1 ? 'bg-[#fbfbfc]' : 'bg-white'
}

function startOfWeek(d: Date): Date {
  const date = new Date(d)
  const day = (date.getDay() + 6) % 7
  date.setDate(date.getDate() - day)
  date.setHours(0, 0, 0, 0)
  return date
}

function getWorkingWindow(tech: Technician): string {
  const wh = tech.working_hours as
    | { start?: unknown; end?: unknown }
    | null
    | undefined

  if (typeof wh?.start !== 'string' || typeof wh?.end !== 'string') return '—'

  return `${wh.start} - ${wh.end}`
}

function getDisplayStatus(tech: Technician): string {
  return tech.display_status ?? tech.status
}

function shouldHideWorkingWindow(tech: Technician): boolean {
  return isTechnicianCurrentlyOnLeave(tech)
}

function formatClockTime(value: string | null | undefined): string {
  if (!value) return '-'
  return value.slice(0, 5)
}

function formatCurrentInterventionDetail(
  tech: Technician,
  t: (key: string, options?: Record<string, unknown>) => string
): string {
  const intervention = tech.current_intervention
  if (!intervention) {
    return t('availability.status.onInterventionDetail')
  }

  const location =
    intervention.address?.trim() || intervention.city?.trim() || ''
  const expectedCompletion = formatClockTime(intervention.end_time)

  if (intervention.client_name && location && expectedCompletion !== '-') {
    return t('availability.status.onInterventionJobDetail', {
      client: intervention.client_name,
      location,
      time: expectedCompletion,
    })
  }

  if (intervention.client_name && location) {
    return t('availability.status.onInterventionClientLocationDetail', {
      client: intervention.client_name,
      location,
    })
  }

  if (intervention.client_name && expectedCompletion !== '-') {
    return t('availability.status.onInterventionClientTimeDetail', {
      client: intervention.client_name,
      time: expectedCompletion,
    })
  }

  if (intervention.client_name) {
    return t('availability.status.onInterventionClientDetail', {
      client: intervention.client_name,
    })
  }

  return t('availability.status.onInterventionDetail')
}

// 2.10.2 — Availability tab restyled to the technician Figma day/week states
// while preserving the existing list query + create-job affordance.
interface TechnicianAvailabilityTabProps {
  listQuery: TechnicianListQuery
  view: ViewMode
  page: number
  pageSize: number
  onPageChange: (page: number) => void
  onPageSizeChange: (size: number) => void
  topTechnicianId: string | null
}

export function TechnicianAvailabilityTab({
  listQuery,
  view,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  topTechnicianId,
}: TechnicianAvailabilityTabProps) {
  const { t, i18n } = useTranslation('technicians')
  const navigate = useNavigate()

  const availabilityListQuery = useMemo(
    () => ({
      ...listQuery,
      top_revenue_first: true,
    }),
    [listQuery]
  )

  const { data, isLoading, isError } = useTechniciansList(availabilityListQuery)
  const technicians = data?.data ?? []

  const today = useMemo(() => {
    const date = new Date()
    date.setHours(0, 0, 0, 0)
    return date
  }, [])

  const currentWeek = useMemo(() => {
    const base = startOfWeek(today)
    return Array.from({ length: 7 }, (_, index) => {
      const date = new Date(base)
      date.setDate(base.getDate() + index)
      return date
    })
  }, [today])

  const dayColumnLabel = useMemo(() => {
    const locale = i18n.language.startsWith('fr') ? 'fr-FR' : 'en-US'
    const label = new Intl.DateTimeFormat(locale, {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
    }).format(today)

    return label.charAt(0).toLocaleUpperCase(locale) + label.slice(1)
  }, [i18n.language, today])
  const goToCreateJob = (techId: string, date: Date) => {
    navigate(
      `/jobs/new?technician_id=${techId}&scheduled_date=${toISODate(date)}`
    )
  }

  const getDayStatus = (tech: Technician, date: Date) => {
    const weekdayIdx = (date.getDay() + 6) % 7
    const worksThatDay = technicianWorksWeekday(tech.working_hours, weekdayIdx)
    const displayStatus = getDisplayStatus(tech)
    const isOnLeave = isTechnicianCurrentlyOnLeave(tech, date)

    if (displayStatus === 'on_intervention') {
      return {
        tone: 'duringIntervention' as const,
        label: t('availability.status.duringIntervention'),
        detail: formatCurrentInterventionDetail(tech, t),
        interactive: false,
      }
    }

    if (isOnLeave) {
      return {
        tone: 'unavailable' as const,
        label: t('availability.unavailable'),
        detail: getTechnicianSecondaryStatusDetail(tech, t),
        interactive: false,
      }
    }

    if (displayStatus === 'inactive' || tech.status === 'inactive') {
      return {
        tone: 'inactive' as const,
        label: t('availability.status.inactive'),
        detail: t('availability.status.inactiveDetail'),
        interactive: false,
      }
    }

    if (!worksThatDay) {
      return {
        tone: 'nonWorking' as const,
        label: t('availability.nonWorking'),
        detail: t('availability.status.nonWorkingDetail'),
        interactive: false,
      }
    }

    return {
      tone: 'available' as const,
      label: t('availability.available'),
      detail: '',
      interactive: true,
    }
  }

  const getWeekStatus = (tech: Technician) => {
    const displayStatus = getDisplayStatus(tech)
    const isOnLeave = isTechnicianCurrentlyOnLeave(tech)

    if (displayStatus === 'on_intervention') {
      return {
        tone: 'duringIntervention' as const,
        label: t('availability.status.duringIntervention'),
        detail: formatCurrentInterventionDetail(tech, t),
      }
    }

    if (isOnLeave) {
      return {
        tone: 'unavailable' as const,
        label: t('availability.unavailable'),
        detail: getTechnicianSecondaryStatusDetail(tech, t),
      }
    }

    if (displayStatus === 'inactive' || tech.status === 'inactive') {
      return {
        tone: 'inactive' as const,
        label: t('availability.status.inactive'),
        detail: t('availability.status.inactiveDetail'),
      }
    }

    return {
      tone: 'available' as const,
      label: t('availability.status.active'),
      detail: '',
    }
  }

  const getWeekChipTone = (tech: Technician, weekdayIdx: number) => {
    const displayStatus = getDisplayStatus(tech)
    if (
      tech.status === 'inactive' ||
      displayStatus === 'inactive' ||
      isTechnicianCurrentlyOnLeave(tech)
    ) {
      return 'inactive' as const
    }

    if (!technicianWorksWeekday(tech.working_hours, weekdayIdx)) {
      return 'nonWorking' as const
    }

    return 'available' as const
  }

  return (
    <div className='flex flex-col gap-6'>
      <Card className='overflow-hidden rounded-2xl border border-[#e7e9ef] bg-white shadow-[0_1px_4px_-4px_rgba(38,45,66,0.05),0_8px_16px_-4px_rgba(38,45,66,0.1)]'>
        <div className='overflow-x-auto'>
          <table className='w-full min-w-[920px] caption-bottom text-sm'>
            <thead className='sticky top-0 z-10 bg-[#fbfbfc]'>
              <tr className='border-0 hover:bg-transparent'>
                <th className={cn(thClass, 'w-[280px] min-w-[280px]')}>
                  {t('availability.technician')}
                </th>
                <th className={cn(thClass, 'w-[180px] min-w-[180px]')}>
                  {view === 'day'
                    ? t('availability.headers.todaySchedule')
                    : t('availability.headers.schedules')}
                </th>
                {view === 'day' ? (
                  <>
                    <th
                      className={cn(
                        thClass,
                        'w-[360px] min-w-[360px] uppercase'
                      )}
                    >
                      {dayColumnLabel}
                    </th>
                    <th className={cn(thClass, 'w-[180px] min-w-[180px]')}>
                      {t('availability.headers.interventionsThisMonth')}
                    </th>
                  </>
                ) : (
                  <>
                    <th className={cn(thClass, 'w-[380px] min-w-[380px]')}>
                      {t('availability.headers.typicalWeek')}
                    </th>
                    <th className={cn(thClass, 'w-[220px] min-w-[220px]')}>
                      {t('availability.headers.status')}
                    </th>
                  </>
                )}
              </tr>
            </thead>

            <tbody>
              {isLoading &&
                Array.from({ length: 6 }).map((_, index) => (
                  <tr key={index} className='bg-white hover:bg-transparent'>
                    <td
                      colSpan={4}
                      className={cn(tdClass, 'px-4 py-5 last:border-b')}
                    >
                      <Skeleton className='h-10 w-full rounded-xl' />
                    </td>
                  </tr>
                ))}

              {isError && !isLoading && (
                <tr className='hover:bg-transparent'>
                  <td
                    colSpan={4}
                    className={cn(
                      tdClass,
                      'px-4 py-10 text-center text-destructive'
                    )}
                  >
                    {t('availability.loadError')}
                  </td>
                </tr>
              )}

              {!isLoading && !isError && technicians.length === 0 && (
                <tr className='hover:bg-transparent'>
                  <td
                    colSpan={4}
                    className={cn(
                      tdClass,
                      'px-4 py-10 text-center text-muted-foreground'
                    )}
                  >
                    {t('availability.empty')}
                  </td>
                </tr>
              )}

              {!isLoading &&
                !isError &&
                technicians.map((tech, index) => {
                  const dayStatus = getDayStatus(tech, today)
                  const weekStatus = getWeekStatus(tech)
                  const workingWindow = shouldHideWorkingWindow(tech)
                    ? '--'
                    : getWorkingWindow(tech)

                  return (
                    <tr
                      key={tech.id}
                      className={cn(
                        'group cursor-pointer transition-colors hover:bg-[#f7f7f9]',
                        getAvailabilityRowClass(index)
                      )}
                      onClick={() => navigate(`/technicians/${tech.id}`)}
                    >
                      <td className={cn(tdClass, 'relative')}>
                        <div className='absolute bottom-0 left-0 top-0 w-0.5 bg-[#ff5a1f] opacity-0 transition-opacity group-hover:opacity-100' />
                        <div className='flex items-center gap-3'>
                          <Avatar
                            name={tech.full_name}
                            src={tech.avatar_url}
                            className='size-8 bg-transparent font-heading text-sm font-semibold text-white'
                            style={
                              tech.avatar_url
                                ? undefined
                                : {
                                    backgroundImage:
                                      AVATAR_GRADIENTS[
                                        index % AVATAR_GRADIENTS.length
                                      ],
                                  }
                            }
                          />
                          <div className='flex min-w-0 items-center gap-2'>
                            <span className={technicianNameTextClass}>
                              {tech.full_name}
                            </span>
                            {tech.id === topTechnicianId ? (
                              <span className={topBadgeClass}>
                                <Star className='size-3 fill-current stroke-current' />
                                {t('availability.topBadge')}
                              </span>
                            ) : null}
                          </div>
                        </div>
                      </td>

                      <td className={cn(tdClass, 'text-[#171a20]')}>
                        <span className='whitespace-nowrap text-sm font-normal leading-[1.4] text-[#171a20]'>
                          {workingWindow}
                        </span>
                      </td>

                      {view === 'day' ? (
                        <>
                          <td className={tdClass}>
                            <div className='flex flex-col gap-1'>
                              {dayStatus.interactive ? (
                                <button
                                  type='button'
                                  onClick={e => {
                                    e.stopPropagation()
                                    goToCreateJob(tech.id, today)
                                  }}
                                  className={cn(
                                    statusBadgeClass,
                                    'transition-transform hover:-translate-y-px focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
                                    dayStatusToneClass[dayStatus.tone]
                                  )}
                                >
                                  {dayStatus.label}
                                </button>
                              ) : (
                                <span
                                  className={cn(
                                    statusBadgeClass,
                                    dayStatusToneClass[dayStatus.tone]
                                  )}
                                >
                                  {dayStatus.label}
                                </span>
                              )}
                              {dayStatus.detail ? (
                                <span className='text-xs leading-[1.4] text-[#7b8190]'>
                                  {dayStatus.detail}
                                </span>
                              ) : null}
                            </div>
                          </td>
                          <td className={cn(tdClass, 'text-[#171a20]')}>
                            <span className='whitespace-nowrap text-sm font-normal leading-[1.4] text-[#171a20]'>
                              {(tech.jobs_completed ?? 0).toLocaleString(
                                'fr-FR'
                              )}
                            </span>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className={tdClass}>
                            <div className='flex flex-wrap gap-1.5'>
                              {currentWeek.map((date, index) => {
                                const label = t(
                                  `availability.days.${DAY_LABEL_KEYS[index]}`
                                )
                                  .slice(0, 1)
                                  .toUpperCase()
                                const tone = getWeekChipTone(tech, index)
                                const isInteractive = tone === 'available'

                                if (isInteractive) {
                                  return (
                                    <button
                                      key={`${tech.id}-${toISODate(date)}`}
                                      type='button'
                                      onClick={e => {
                                        e.stopPropagation()
                                        goToCreateJob(tech.id, date)
                                      }}
                                      className={cn(
                                        'inline-flex size-6 items-center justify-center rounded-full text-xs font-semibold leading-none transition-transform hover:-translate-y-px focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
                                        weekChipToneClass[tone]
                                      )}
                                      title={t('availability.createJob')}
                                    >
                                      {label}
                                    </button>
                                  )
                                }

                                return (
                                  <span
                                    key={`${tech.id}-${toISODate(date)}`}
                                    className={cn(
                                      'inline-flex size-6 items-center justify-center rounded-full text-xs font-semibold leading-none',
                                      weekChipToneClass[tone]
                                    )}
                                  >
                                    {label}
                                  </span>
                                )
                              })}
                            </div>
                          </td>
                          <td className={tdClass}>
                            <div className='flex flex-col gap-1'>
                              <span
                                className={cn(
                                  statusBadgeClass,
                                  dayStatusToneClass[weekStatus.tone]
                                )}
                              >
                                {weekStatus.label}
                              </span>
                              {weekStatus.detail ? (
                                <span className='text-xs leading-[1.4] text-[#7b8190]'>
                                  {weekStatus.detail}
                                </span>
                              ) : null}
                            </div>
                          </td>
                        </>
                      )}
                    </tr>
                  )
                })}
            </tbody>
          </table>
        </div>

        <ListPagination
          metadata={data?.metadata}
          page={page}
          pageSize={pageSize}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          showSummary={false}
        />
      </Card>
    </div>
  )
}

export default TechnicianAvailabilityTab
